import React, { Component } from "react";
import { withStyles } from "material-ui/styles";
import Button from "material-ui/Button";
import TextField from "material-ui/TextField";
import * as moment from "moment";
import ApiAutoFetchDatagrid from "../controls/datagrid/ApiAutoFetchDatagrid";
import api from "../../api";

const styles = () => ({
  wrapper: {
    width: "100%",
    boxSizing: "border-box",
    marginTop: 20,
    padding: 10
  },
  filters: {
    display: "flex",
    alignItems: "flex-end",
    gap: 10,
    marginBottom: 20,
    flexWrap: "wrap"
  },
  field: {
    minWidth: 180
  },
  button: {
    marginBottom: 1
  },
  summary: {
    display: "flex",
    gap: 15,
    flexWrap: "wrap",
    marginBottom: 20
  },
  summaryCard: {
    minWidth: 180,
    flex: "1 1 180px",
    padding: "18px 20px",
    borderRadius: 12,
    boxSizing: "border-box",
    background: "#ffffff",
    boxShadow: "0 4px 14px rgba(0, 0, 0, 0.08)",
    border: "1px solid #e5e7eb",
    borderLeft: "5px solid #3f51b5"
  },
  totalSalesCard: {
    background: "#ecfdf5",
    borderLeftColor: "#10b981"
  },
  transactionsCard: {
    background: "#eff6ff",
    borderLeftColor: "#3b82f6"
  },
  cashCard: {
    background: "#f0fdf4",
    borderLeftColor: "#22c55e"
  },
  transferCard: {
    background: "#f5f3ff",
    borderLeftColor: "#8b5cf6"
  },
  cardPaymentCard: {
    background: "#fff7ed",
    borderLeftColor: "#f97316"
  },
  summaryLabel: {
    fontSize: 13,
    color: "#64748b",
    marginBottom: 8,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.4px"
  },
  summaryValue: {
    fontSize: 22,
    fontWeight: 700,
    color: "#111827"
  }
});

class TodaysSales extends Component {
  state = {
    startDate: moment().format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
    appliedStartDate: moment().format("YYYY-MM-DD"),
    appliedEndDate: moment().format("YYYY-MM-DD"),

    summary: {
      totalSales: 0,
      transactions: 0,
      cash: 0,
      transfer: 0,
      card: 0
    }
  };

  salesColumns = [
    "ID",
    "Time",
    "Cashier",
    "Items",
    "Total",
    "Amount Paid",
    "Payment Method",
    "Payment Breakdown"
  ];

  getApiPromise = async () => {
    const { appliedStartDate, appliedEndDate } = this.state;

    const today = moment().format("YYYY-MM-DD");

    let response;

    if (appliedStartDate === today && appliedEndDate === today) {
      response = await api.transaction.getTodaysSales();
    } else {
      response = await api.transaction.getSalesHistory(
        appliedStartDate,
        appliedEndDate
      );
    }

    const sales = response.data || [];

    let totalSales = 0;
    let cash = 0;
    let transfer = 0;
    let card = 0;

    sales.forEach(sale => {
      totalSales += Number(sale.billAmount || 0);

      if (sale.paymentBreakdown && sale.paymentBreakdown.length > 0) {
        sale.paymentBreakdown.forEach(payment => {
          const method = String(payment.paymentMethod || "").toUpperCase();
          const amount = Number(payment.amount || 0);

          if (method === "CASH") {
            cash += amount;
          } else if (method === "TRANSFER") {
            transfer += amount;
          } else if (method === "CARD") {
            card += amount;
          }
        });
      } else {
        const method = String(sale.paymentMethod || "").toUpperCase();
        const amount = Number(sale.amountPaid || sale.billAmount || 0);

        if (method === "CASH") {
          cash += amount;
        } else if (method === "TRANSFER") {
          transfer += amount;
        } else if (method === "CARD") {
          card += amount;
        }
      }
    });

    this.setState({
      summary: {
        totalSales,
        transactions: sales.length,
        cash,
        transfer,
        card
      }
    });

    response.data = sales.map(sale => ({
      id: sale.id,
      time: sale.createdAt,
      cashier: sale.waiter || "-",
      items:
        sale.goodsBought && sale.goodsBought.length > 0
          ? sale.goodsBought
              .map(item => `${item.name} x${item.qty}`)
              .join(", ")
          : "-",
      total: sale.billAmount,
      amountPaid: sale.amountPaid,
      paymentMethod: sale.paymentMethod || "-",
      paymentBreakdown:
        sale.paymentBreakdown && sale.paymentBreakdown.length > 0
          ? sale.paymentBreakdown
              .map(
                payment =>
                  `${payment.paymentMethod}: NGN ${Number(
                    payment.amount || 0
                  ).toLocaleString()}`
              )
              .join(" + ")
          : "-"
    }));

    return response;
  };

  handleChange = field => event => {
    this.setState({
      [field]: event.target.value
    });
  };

  applyFilter = () => {
    const { startDate, endDate } = this.state;

    this.setState({
      appliedStartDate: startDate,
      appliedEndDate: endDate
    });
  };

  showToday = () => {
    const today = moment().format("YYYY-MM-DD");

    this.setState({
      startDate: today,
      endDate: today,
      appliedStartDate: today,
      appliedEndDate: today
    });
  };

  transformers = {
    time: value => moment(value).format("DD-MM-YYYY HH:mm"),
    total: value => `NGN ${Number(value || 0).toLocaleString()}`,
    amountPaid: value => `NGN ${Number(value || 0).toLocaleString()}`
  };

  render() {
    const { classes } = this.props;
    const { startDate, endDate, summary } = this.state;

    const money = value =>
      `NGN ${Number(value || 0).toLocaleString()}`;

    return (
      <div className={classes.wrapper}>
        <div className={classes.filters}>
          <TextField
            className={classes.field}
            label="Start Date"
            type="date"
            value={startDate}
            onChange={this.handleChange("startDate")}
            InputLabelProps={{
              shrink: true
            }}
          />

          <TextField
            className={classes.field}
            label="End Date"
            type="date"
            value={endDate}
            onChange={this.handleChange("endDate")}
            InputLabelProps={{
              shrink: true
            }}
          />

          <Button
            className={classes.button}
            variant="contained"
            color="primary"
            onClick={this.applyFilter}
          >
            Apply Filter
          </Button>

          <Button
            className={classes.button}
            variant="outlined"
            onClick={this.showToday}
          >
            Today
          </Button>
        </div>

        <div className={classes.summary}>
          <div className={`${classes.summaryCard} ${classes.totalSalesCard}`}>
            <div className={classes.summaryLabel}>
              Total Sales
            </div>
            <div className={classes.summaryValue}>
              {money(summary.totalSales)}
            </div>
          </div>

          <div className={`${classes.summaryCard} ${classes.transactionsCard}`}>
            <div className={classes.summaryLabel}>
              Transactions
            </div>
            <div className={classes.summaryValue}>
              {summary.transactions}
            </div>
          </div>

          <div className={`${classes.summaryCard} ${classes.cashCard}`}>
            <div className={classes.summaryLabel}>
              Cash
            </div>
            <div className={classes.summaryValue}>
              {money(summary.cash)}
            </div>
          </div>

          <div className={`${classes.summaryCard} ${classes.transferCard}`}>
            <div className={classes.summaryLabel}>
              Transfer
            </div>
            <div className={classes.summaryValue}>
              {money(summary.transfer)}
            </div>
          </div>

          <div className={`${classes.summaryCard} ${classes.cardPaymentCard}`}>
            <div className={classes.summaryLabel}>
              Card
            </div>
            <div className={classes.summaryValue}>
              {money(summary.card)}
            </div>
          </div>
        </div>

        <ApiAutoFetchDatagrid
          datasourcePromise={this.getApiPromise}
          headers={this.salesColumns}
          actions={[]}
          transformers={this.transformers}
        />
      </div>
    );
  }
}

export default withStyles(styles)(TodaysSales);



